<template>
  <div class="game-list">
    <el-table
      ref="table"
      :data="sortedGameList"
      style="width: 100%"
      :border="false"
      stripe
      :row-key="row => row.game_code"
      :expand-row-keys="Array.from(expandedRows)"
      @expand-change="handleExpandChange"
      @sort-change="handleSortChange"
      @selection-change="handleSelectionChange"
      :default-sort="{prop: 'game_time', order: 'descending'}"
      :header-cell-style="{
        background: '#f2f3f5',
        color: '#606266',
        fontWeight: '600',
        fontSize: '14px',
        height: '52px',
        padding: '0',
        borderBottom: '1px solid #DCDFE6'
      }"
      :row-style="({ row, rowIndex }) => {
        return {
          background: rowIndex % 2 === 0 ? '#ffffff' : '#fafafa'
        }
      }"
      height="calc(100vh - 42px - 52px - 120px - 120px)"
      v-loading="loading">
      
      <el-table-column
        type="selection"
        width="50"
        align="center">
      </el-table-column>
      
      <el-table-column
        prop="seq_number"
        label="序号"
        width="50"
        align="center"
        v-if="showColumns.seq_number">
        <template slot-scope="scope">
          {{ scope.$index + 1 }}
        </template>
      </el-table-column>
      
      <el-table-column
        prop="game_name"
        label="赛事名称"
        min-width="200"
        align="left"
        :sortable="'custom'"
        v-if="showColumns.game_name">
        <template slot-scope="scope">
          <div class="game-name-cell">
            <template v-if="scope.row.replay_status === '已完成'">
              <i class="el-icon-video-camera completed" 
                @click.stop="handleShowReplay(scope.row)"
                style="cursor: pointer; margin-right: 4px; color: #F56C6C;">
              </i>
            </template>
            <el-tooltip 
              :content="scope.row.game_name" 
              placement="top" 
              :open-delay="300"
              :disabled="!scope.row.game_name || scope.row.game_name.length < 20">
              <div class="game-name-text">
                <a v-if="scope.row.game_url" 
                   :href="scope.row.game_url" 
                   target="_blank"
                   class="game-name-link">
                  {{ scope.row.game_name }}
                </a>
                <span v-else>{{ scope.row.game_name }}</span>
              </div>
            </el-tooltip>
          </div>
        </template>
      </el-table-column>
      
      <el-table-column
        prop="game_type"
        label="赛事类型"
        width="120"
        align="left"
        :sortable="'custom'"
        v-if="showColumns.game_type">
      </el-table-column>
      
      <el-table-column
        prop="game_level"
        label="赛事级别"
        width="100"
        align="left"
        :sortable="'custom'"
        v-if="showColumns.game_level">
      </el-table-column>
      
      <el-table-column
        prop="game_scale"
        label="赛事规模"
        width="120"
        align="left"
        :sortable="'custom'"
        v-if="showColumns.game_scale">
      </el-table-column>
      
      <el-table-column
        prop="game_org"
        label="赛事机构"
        width="120"
        align="left"
        :sortable="'custom'"
        v-if="showColumns.game_org">
      </el-table-column>
      
      <el-table-column
        prop="game_time"
        label="赛事时间"
        width="100"
        align="left"
        :sortable="'custom'"
        v-if="showColumns.game_time">
        <template slot-scope="scope">
          {{ formatDate(scope.row.game_time) }}
        </template>
      </el-table-column>
      
      <el-table-column
        prop="question_num"
        label="题数"
        width="75"
        align="center"
        :sortable="'custom'"
        v-if="showColumns.question_num">
        <template slot-scope="scope">
          {{ scope.row.question_num === undefined || scope.row.question_num === null ? 'Null' : formatNumber(scope.row.question_num) }}
        </template>
      </el-table-column>
      
      <el-table-column
        prop="total_score"
        label="分数"
        width="90"
        align="center"
        :sortable="'custom'"
        v-if="showColumns.total_score">
        <template slot-scope="scope">
          <div style="display: flex; align-items: center; justify-content: center; gap: 4px;">
            <span :style="{
              fontWeight: 'bold',
              color: getScoreColor(scope.row.total_score, scope.row.question_num)
            }">
              {{ formatNumber(scope.row.total_score || 0) }}
            </span>
            <span>/</span>
            <span :style="{
              fontWeight: 'bold',
              color: getScoreColor(scope.row.total_score_fix, scope.row.question_num)
            }">
              {{ formatNumber(scope.row.total_score_fix || 0) }}
            </span>
          </div>
        </template>
      </el-table-column>
      
      <el-table-column
        prop="parameter_number"
        label="人数"
        width="75"
        align="right"
        :sortable="'custom'"
        v-if="showColumns.parameter_number">
        <template slot-scope="scope">
          {{ scope.row.parameter_number === undefined || scope.row.parameter_number === null ? 'Null' : formatNumber(scope.row.parameter_number) }}
        </template>
      </el-table-column>
      
      <el-table-column
        prop="rank"
        label="名次"
        width="75"
        align="right"
        :sortable="'custom'"
        v-if="showColumns.rank">
        <template slot-scope="scope">
          {{ scope.row.rank === undefined || scope.row.rank === null ? 'Null' : formatNumber(scope.row.rank) }}
        </template>
      </el-table-column>
      
      <!-- 难度列 -->
      <el-table-column
        prop="avg_difficulty"
        label="难度"
        width="80"
        align="center"
        :sortable="'custom'"
        class-name="difficulty-column"
        v-if="showColumns.avg_difficulty">
        <template slot="header" slot-scope="scope">
          <span>难度</span>
          <el-tooltip effect="dark" placement="top" popper-class="difficulty-tooltip">
            <div slot="content" class="difficulty-tooltip-content">
              <div class="difficulty-levels">
                <div class="difficulty-level">
                  <span style="display:inline-block;width:16px;height:16px;border-radius:3px;margin-right:8px;background-color:#F56C6C;"></span>
                  <span class="level-range">00.00 ~ 14.29%:</span>
                  <span class="level-name">红题（很简单）</span>
                </div>
                <div class="difficulty-level">
                  <span style="display:inline-block;width:16px;height:16px;border-radius:3px;margin-right:8px;background-color:#E6A23C;"></span>
                  <span class="level-range">14.29 ~ 28.58%:</span>
                  <span class="level-name">橙题（简单）</span>
                </div>
                <div class="difficulty-level">
                  <span style="display:inline-block;width:16px;height:16px;border-radius:3px;margin-right:8px;background-color:#F7BA2A;"></span>
                  <span class="level-range">28.58 ~ 42.87%:</span>
                  <span class="level-name">黄题（较易）</span>
                </div>
                <div class="difficulty-level">
                  <span style="display:inline-block;width:16px;height:16px;border-radius:3px;margin-right:8px;background-color:#67C23A;"></span>
                  <span class="level-range">42.87 ~ 57.15%:</span>
                  <span class="level-name">绿题（中等）</span>
                </div>
                <div class="difficulty-level">
                  <span style="display:inline-block;width:16px;height:16px;border-radius:3px;margin-right:8px;background-color:#409EFF;"></span>
                  <span class="level-range">57.15 ~ 71.44%:</span>
                  <span class="level-name">蓝题（较难）</span>
                </div>
                <div class="difficulty-level">
                  <span style="display:inline-block;width:16px;height:16px;border-radius:3px;margin-right:8px;background-color:#8E44AD;"></span>
                  <span class="level-range">71.44 ~ 85.72%:</span>
                  <span class="level-name">紫题（很难）</span>
                </div>
                <div class="difficulty-level">
                  <span style="display:inline-block;width:16px;height:16px;border-radius:3px;margin-right:8px;background-color:#2C3E50;"></span>
                  <span class="level-range">85.72 ~ 100%:</span>
                  <span class="level-name">黑题（极难）</span>
                </div>
              </div>
            </div>
            <i class="el-icon-question" style="cursor: pointer; margin-left: 4px; font-size: 14px;"></i>
          </el-tooltip>
        </template>
        <template slot-scope="scope">
          <template v-if="scope.row.avg_difficulty !== null">
            <el-tag
              size="small"
              class="auto-width-tag"
              :style="{
                backgroundColor: getDifficultyColorByPercentage(scope.row.avg_difficulty),
                borderColor: getDifficultyColorByPercentage(scope.row.avg_difficulty),
                color: 'white'
              }">
              {{ formatDifficultyPercentage(scope.row.avg_difficulty) }}
            </el-tag>
          </template>
          <span v-else>未定义</span>
        </template>
      </el-table-column>
      
      <el-table-column
        prop="score_rate"
        label="得分率"
        width="80"
        align="center"
        :sortable="'custom'"
        class-name="score-rate-column"
        v-if="showColumns.score_rate">
        <template slot-scope="scope">
          <el-tag
            :type="getScoreRateTagType(scope.row.score_rate, scope.row)"
            size="small"
            class="auto-width-tag"
            :style="getScoreRateStyle(scope.row.score_rate, scope.row)">
            {{ getScoreRateText(scope.row) }}
          </el-tag>
        </template>
      </el-table-column>
      
      <el-table-column
        prop="rank_rate"
        label="排位"
        width="80"
        align="center"
        :sortable="'custom'"
        class-name="rank-rate-column"
        v-if="showColumns.rank_rate">
        <template slot-scope="scope">
          <el-tag
            :type="getRankRateTagType(scope.row.rank_rate, scope.row)"
            size="small"
            class="auto-width-tag"
            :style="getRankRateStyle(scope.row.rank_rate, scope.row)">
            {{ getRankRateText(scope.row) }}
          </el-tag>
        </template>
      </el-table-column>
      
      <el-table-column
        prop="fix_status"
        label="订正"
        width="65"
        align="center"
        class-name="fix-status-column"
        :sortable="'custom'">
        <template slot-scope="scope">
          <el-tag
            :type="scope.row.fix_status === '已完成' ? 'success' : 
                   scope.row.fix_status === '未完成' ? 'warning' : 'info'"
            :effect="scope.row.fix_status === '未添加' ? 'plain' : 'light'"
            size="small"
            class="auto-width-tag"
            :style="{
              backgroundColor: scope.row.fix_status === '已完成' ? '#f0f9eb' :
                             scope.row.fix_status === '未完成' ? '#fef5eb' : '#f5f7fa',
              borderColor: scope.row.fix_status === '已完成' ? '#b3e19d' :
                          scope.row.fix_status === '未完成' ? '#ffd591' : '#e4e7ed',
              color: scope.row.fix_status === '已完成' ? '#55a532' :
                     scope.row.fix_status === '未完成' ? '#d48806' : '#666666'
            }">
            <span v-if="scope.row.fix_status === '已完成'">✅</span>
            <span v-if="scope.row.fix_status === '未完成'">⬜</span>
            <span v-if="scope.row.fix_status === '未添加'">❓</span>
          </el-tag>
        </template>
      </el-table-column>
      
      <el-table-column
        prop="replay_status"
        label="复盘"
        width="65"
        align="center"
        class-name="replay-status-column"
        :sortable="'custom'"
        v-if="showColumns.replay_status">
        <template slot-scope="scope">
          <el-tag
            :type="scope.row.replay_status === '已完成' ? 'success' : 'warning'"
            size="small"
            class="auto-width-tag"
            :style="{
              backgroundColor: scope.row.replay_status === '已完成' ? '#f0f9eb' : '#fef5eb',
              borderColor: scope.row.replay_status === '已完成' ? '#b3e19d' : '#ffd591',
              color: scope.row.replay_status === '已完成' ? '#55a532' : '#d48806'
            }">
            <span v-if="scope.row.replay_status === '已完成'">✅</span>
            <span v-if="scope.row.replay_status === '未完成'">⬜</span>
          </el-tag>
        </template>
      </el-table-column>
      
      <el-table-column
        prop="note"
        label="备注"
        min-width="120"
        align="left"
        :sortable="'custom'"
        v-if="showColumns.note">
        <template slot-scope="scope">
          {{ scope.row.note || '-' }}
        </template>
      </el-table-column>
      
      <!-- 展开列（隐藏） -->
      <el-table-column type="expand" width="1" class="hidden-expand-column">
        <template slot-scope="scope">
          <question-list
            v-if="questionsCache.has(scope.row.game_code)"
            :game-code="scope.row.game_code"
            :questions="questionsCache.get(scope.row.game_code)"
            @refresh="handleQuestionRefreshComplete"
            @question-operation-complete="handleQuestionOperationComplete"
            @edit="handleQuestionEdit"
            @delete="handleQuestionDelete"
            @fix="handleQuestionFix"
          />
        </template>
      </el-table-column>
      
      <!-- 操作列 -->
      <el-table-column
        label="操作"
        width="160"
        align="center"
        fixed="right"
        class-name="operation-column">
        <template slot="header" slot-scope="scope">
          <span style="display: inline-block; text-align: center; width: 100%;">操作</span>
        </template>
        <template slot-scope="scope">
          <div class="operation-buttons">
            <el-tooltip content="展开" placement="top" :enterable="false" :open-delay="100">
              <el-button
                type="text"
                class="operation-button expand-button"
                @click.stop="handleExpand(scope.$index, scope.row)">
                <i :class="isExpanded(scope.row) ? 'el-icon-arrow-down' : 'el-icon-arrow-right'"></i>
              </el-button>
            </el-tooltip>
            
            <el-tooltip content="编辑" placement="top" :enterable="false" :open-delay="100">
              <el-button
                type="text"
                class="operation-button edit-button"
                @click.stop="$emit('edit', scope.row)">
                <i class="el-icon-edit"></i>
              </el-button>
            </el-tooltip>
            
            <el-tooltip content="录题" placement="top" :enterable="false" :open-delay="100">
              <el-button
                type="text"
                class="operation-button add-button"
                @click.stop="$emit('add-question', scope.row)">
                <i class="el-icon-plus"></i>
              </el-button>
            </el-tooltip>
            
            <el-tooltip content="复盘" placement="top" :enterable="false" :open-delay="100">
              <el-button
                type="text"
                class="operation-button"
                :class="scope.row.replay_status === '已完成' ? 'disabled-button' : 'replay-button'"
                :disabled="scope.row.replay_status === '已完成'"
                @click.stop="handleReplay(scope.row)">
                <i class="el-icon-video-camera"></i>
              </el-button>
            </el-tooltip>
          </div>
        </template>
      </el-table-column>
    </el-table>

    <div class="pagination-container">
      <div class="pagination-left">
        <el-popover
          placement="top-start"
          width="300"
          trigger="click"
          popper-class="column-selector-popover"
          :popper-options="{ boundariesElement: 'viewport' }">
          <div class="popover-content-wrapper" style="max-height: 60vh; overflow-y: auto;">
            <el-form label-width="100px">
              <div class="column-options">
                <el-form-item label="序号">
                  <el-switch v-model="showColumns.seq_number"></el-switch>
                </el-form-item>
                <el-form-item label="赛事名称">
                  <el-switch v-model="showColumns.game_name"></el-switch>
                </el-form-item>
                <el-form-item label="赛事类型">
                  <el-switch v-model="showColumns.game_type"></el-switch>
                </el-form-item>
                <el-form-item label="赛事级别">
                  <el-switch v-model="showColumns.game_level"></el-switch>
                </el-form-item>
                <el-form-item label="赛事规模">
                  <el-switch v-model="showColumns.game_scale"></el-switch>
                </el-form-item>
                <el-form-item label="赛事机构">
                  <el-switch v-model="showColumns.game_org"></el-switch>
                </el-form-item>
                <el-form-item label="赛事时间">
                  <el-switch v-model="showColumns.game_time"></el-switch>
                </el-form-item>
                <el-form-item label="题数">
                  <el-switch v-model="showColumns.question_num"></el-switch>
                </el-form-item>
                <el-form-item label="分数">
                  <el-switch v-model="showColumns.total_score"></el-switch>
                </el-form-item>
                <el-form-item label="参赛人数">
                  <el-switch v-model="showColumns.parameter_number"></el-switch>
                </el-form-item>
                <el-form-item label="排名">
                  <el-switch v-model="showColumns.rank"></el-switch>
                </el-form-item>
                <el-form-item label="难度">
                  <el-switch v-model="showColumns.avg_difficulty"></el-switch>
                </el-form-item>
                <el-form-item label="得分率">
                  <el-switch v-model="showColumns.score_rate"></el-switch>
                </el-form-item>
                <el-form-item label="排名率">
                  <el-switch v-model="showColumns.rank_rate"></el-switch>
                </el-form-item>
                <el-form-item label="订正状态">
                  <el-switch v-model="showColumns.fix_status"></el-switch>
                </el-form-item>
                <el-form-item label="复盘状态">
                  <el-switch v-model="showColumns.replay_status"></el-switch>
                </el-form-item>
                <el-form-item label="备注">
                  <el-switch v-model="showColumns.note"></el-switch>
                </el-form-item>
              </div>
            </el-form>
          </div>
          <el-button slot="reference" type="primary">
            <i class="el-icon-s-operation"></i> 显示列
          </el-button>
        </el-popover>
      </div>
      <el-pagination
        background
        :current-page.sync="currentPage"
        :page-sizes="[20, 50, 100]"
        :page-size.sync="pageSize"
        layout="total, sizes, prev, pager, next, jumper"
        :total="total"
        @size-change="handleSizeChange"
        @current-change="handleCurrentChange">
      </el-pagination>
    </div>

    <game-replay-view
      :visible.sync="replayViewVisible"
      :game-info="selectedGame || {}"
      @refresh="handleReplayRefresh"
    />
  </div>
</template>

<script>
import QuestionList from './QuestionList.vue'
import GameReplayView from './GameReplayView.vue'
import request from '@/utils/request'

export default {
  name: 'GameList',
  components: {
    QuestionList,
    GameReplayView
  },
  props: {
    loading: {
      type: Boolean,
      default: false
    },
    gameList: {
      type: Array,
      default: () => []
    },
    showColumnDialog: {
      type: Boolean,
      default: false
    },
    currentPage: {
      type: Number,
      required: true
    },
    pageSize: {
      type: Number,
      required: true
    },
    total: {
      type: Number,
      required: true
    }
  },
  created() {
    // 从本地存储加载列显示设置
    const savedColumns = localStorage.getItem('gameListColumns')
    if (savedColumns) {
      this.showColumns = JSON.parse(savedColumns)
    }
  },
  mounted() {
    // 从本地存储加载列显示设置
    const savedColumns = localStorage.getItem('gameListColumns')
    if (savedColumns) {
      this.showColumns = JSON.parse(savedColumns)
    }
    
    // 调试：检查gameList数据结构
    this.$nextTick(() => {
      if (this.gameList && this.gameList.length > 0) {
        console.log('GameList数据结构示例:', this.gameList[0])
        console.log('是否包含total_score_fix字段:', this.gameList[0].hasOwnProperty('total_score_fix'))
      }
    })
  },
  watch: {
    gameList: {
      handler(newVal) {
        if (newVal && newVal.length > 0) {
          console.log('GameList首条数据示例:', newVal[0])
          console.log('是否包含total_score_fix字段:', newVal[0].hasOwnProperty('total_score_fix'))
        }
      },
      immediate: true
    },
    showColumnDialog(val) {
      this.columnDialogVisible = val
    },
    columnDialogVisible(val) {
      this.$emit('update:showColumnDialog', val)
    },
    showColumns: {
      handler(val) {
        // 保存显示设置到本地存储
        localStorage.setItem('gameListColumns', JSON.stringify(val))
      },
      deep: true
    },
    filterOptions: {
      handler(newOptions) {
        // 当过滤选项更新时确保所有选项被选中
        this.filterParams = {
          ...this.filterParams,
          game_type: [...(newOptions.game_type || [])],
          game_level: [...(newOptions.game_level || [])],
          game_scale: [...(newOptions.game_scale || [])],
          game_org: [...(newOptions.game_org || [])],
          fix_status: [...(newOptions.fix_status || [])],
          replay_status: [...(newOptions.replay_status || [])]
        }
      },
      deep: true,
      immediate: true
    }
  },
  data() {
    return {
      questionsCache: new Map(),
      showColumns: {
        seq_number: true,
        game_name: true,
        game_type: true,
        game_level: true,
        game_scale: false,
        game_org: false,
        game_time: true,
        question_num: true,
        total_score: true,
        parameter_number: true,
        rank: true,
        avg_difficulty: true,
        score_rate: true,
        rank_rate: true,
        fix_status: true,
        replay_status: true,
        note: false
      },
      columnDialogVisible: false,
      expandedRows: new Set(),
      filterParams: {
        game_name: '',
        game_type: [],
        game_level: [],
        game_scale: [],
        game_org: [],
        fix_status: [],
        replay_status: []
      },
      filterOptions: {
        game_type: [],
        game_level: [],
        game_scale: [],
        game_org: [],
        fix_status: ['已完成', '未完成'],
        replay_status: ['已完成', '未完成']
      },
      isIndeterminate: {
        game_type: false,
        game_level: false,
        game_scale: false,
        game_org: false,
        fix_status: false,
        replay_status: false
      },
      showGameNameFilter: false,
      replayViewVisible: false,
      selectedGame: null
    }
  },
  computed: {
    isOverdueAndUnfixed() {
      return (row) => {
        try {
          if (!row || !row.game_time) return false
          if (row.fix_status === '已完成') return false
          
          const gameTime = new Date(row.game_time)
          const now = new Date()
          const diffDays = Math.floor((now - gameTime) / (1000 * 60 * 60 * 24))
          return diffDays > 7
        } catch (error) {
          console.error('检查是否超期未订正时出错:', error)
          return false
        }
      }
    },
    getFixStatus() {
      return (row) => {
        try {
          if (!row || !row.game_code) return false
          
          const questions = this.questionsCache.get(row.game_code) || []
          if (!questions.length) return false
          
          return questions.every(q => q.question_score === 100 || q.question_score_fix === 100)
        } catch (error) {
          console.error('Error in getFixStatus:', error)
          return false
        }
      }
    },
    sortedGameList() {
      // 直接使用父组件传入的已排序和分页的数据
      return this.gameList
    }
  },
  methods: {
    // 预加载当前页面的赛事题目数据（保留原方法以兼容现有代码）
    async preloadAllQuestionsData() {
      try {
        if (!this.gameList || this.gameList.length === 0) return;
        
        console.log('开始预加载当前页面赛事的题目数据...');
        
        // 创建一个队列，限制并发请求数量
        const queue = [...this.gameList]; // 只加载当前页面的数据
        const concurrentLimit = 3; // 最大并发数
        const promises = [];
        
        // 处理队列中的请求
        const processQueue = async () => {
          while (queue.length > 0) {
            const game = queue.shift();
            if (!game || !game.game_code) continue;
            
            // 如果缓存中已有数据，则跳过
            if (this.questionsCache.has(game.game_code)) continue;
            
            // 加载数据
            try {
              await this.loadQuestions(game.game_code);
              console.log(`成功加载赛事题目数据: ${game.game_code}`);
            } catch (error) {
              console.error(`加载赛事题目数据失败: ${game.game_code}`, error);
            }
          }
        };
        
        // 启动多个并发处理
        for (let i = 0; i < Math.min(concurrentLimit, queue.length); i++) {
          promises.push(processQueue());
        }
        
        // 等待所有处理完成
        await Promise.all(promises);
        console.log('当前页面赛事题目数据预加载完成');
        
        // 强制更新视图
        this.$forceUpdate();
      } catch (error) {
        console.error('预加载赛事题目数据时出错:', error);
      }
    },
    handleSelectionChange(selection) {
      this.$emit('selection-change', selection)
    },
    
    handleSortChange({ column, prop, order }) {
      console.log('Sort changed:', { prop, order });
      
      // 触发父组件的排序事件
      this.$emit('sort-change', {
        prop: prop || 'game_time',
        order: order || 'descending'
      });
    },
    async handleExpand(index, row) {
      try {
        const isCurrentlyExpanded = this.expandedRows.has(row.game_code)
        
        // 如果当前是收起状态，则展开并加载数据
        if (!isCurrentlyExpanded) {
          // 先加载数据
          if (!this.questionsCache.has(row.game_code)) {
            console.log('开始加载题目数据:', row.game_code)
            await this.loadQuestions(row.game_code)
          }
          // 添加到展开集合
          this.expandedRows.add(row.game_code)
        } else {
          // 从展开集合中移除
          this.expandedRows.delete(row.game_code)
        }
        
        // 切换展开状态
        this.$refs.table.toggleRowExpansion(row)
      } catch (error) {
        console.error('展开行操作失败:', error)
        this.$message.error('展开行操作失败，请重试')
        // 确保状态一致
        this.expandedRows.delete(row.game_code)
        this.$refs.table.toggleRowExpansion(row, false)
      }
    },
    async loadQuestions(gameCode) {
      try {
        console.log('开始加载题目列表:', gameCode)
        const response = await request.get(`/api/game/questions/${gameCode}`)
        console.log('获取到题目数据:', response)
        
        if (response && response.code === 200 && Array.isArray(response.data)) {
          this.questionsCache.set(gameCode, response.data)
          return true
        }
        return false
      } catch (error) {
        console.error('加载题目列表失败:', error)
        return false
      }
    },
    async handleQuestionRefreshComplete(gameCode) {
      try {
        console.log('开始刷新题目列表:', gameCode)
        // 清除缓存
        this.questionsCache.delete(gameCode)
        // 重新加载题目数据
        const success = await this.loadQuestions(gameCode)
        if (success) {
          // 找到对应的题目列表组件并刷新
          const questionLists = this.$refs.questionList
          if (questionLists) {
            const lists = Array.isArray(questionLists) ? questionLists : [questionLists]
            for (const list of lists) {
              if (list && list.gameCode === gameCode) {
                await list.refresh()
              }
            }
          }
          // 刷新表格布局
          this.$refs.table.doLayout()
          console.log('题目列表刷新完成')
          // 通知父组件刷新
          this.$emit('refresh')
        }
      } catch (error) {
        console.error('刷新题目列表失败:', error)
        // 只记录错误，不抛出
      }
    },
    isExpanded(row) {
      return this.expandedRows.has(row.game_code)
    },
    handleExpandChange(row, expandedRows) {
      this.$emit('expand-change', row, expandedRows)
    },
    handleAddQuestion(row) {
      this.$emit('add-question', row)
    },
    openUrl(url) {
      if (url) {
        window.open(url, '_blank')
      }
    },
    formatRankRate(value) {
      // 如果值无效，返回 Null
      if (value === undefined || value === null) return 'Null';
      
      // 转换为数字
      const numValue = parseFloat(value);
      
      // 如果转换失败，返回原值
      if (isNaN(numValue)) return value;
      
      // 保留两位小数
      return numValue.toFixed(2) + '%';
    },
    handleQuestionEdit(question) {
      this.$emit('question-edit', question)
    },
    handleQuestionDelete(question) {
      this.$emit('question-delete', question)
    },
    handleQuestionFix(question) {
      this.$emit('question-fix', question)
    },
    handleSizeChange(val) {
      this.$emit('size-change', val)
    },
    handleCurrentChange(val) {
      this.$emit('current-change', val)
    },
    handleColumnConfirm() {
      this.columnDialogVisible = false
    },
    toggleColumnSelector() {
      this.columnDialogVisible = !this.columnDialogVisible
    },
    formatDate(date) {
      if (!date) return ''
      const d = new Date(date)
      if (isNaN(d.getTime())) return date
      return d.toLocaleDateString('zh-CN')
    },
    async handleReplay(row) {
      try {
        if (!row || !row.game_code) {
          console.error('无效的赛事数据:', JSON.stringify(row))
          this.$message.error('无效的赛事数据')
          return
        }

        // 如果已经复盘过，直接返回
        if (row.replay_status === '已完成') {
          return
        }

        // 触发父组件的复盘事件
        this.$emit('replay', row)
      } catch (error) {
        console.error('处理复盘操作时出错:', error)
        this.$message.error('复盘操作失败')
      }
    },
    formatNumber(value) {
      if (value === undefined || value === null) return 'Null'
      return Number(value).toLocaleString('zh-CN')
    },
    handleRowClick(row) {
      this.handleExpand(row)
    },
    handlePopoverShow(field) {
      // 如果首次打开,获取过滤项
      if (this.filterOptions[field].length === 0 && field !== 'fix_status' && field !== 'replay_status') {
        const values = [...new Set(this.tableData.map(item => item[field]))].filter(Boolean)
        this.filterOptions[field] = values
      }
    },
    
    handlePopoverClose(field) {
      // 关闭弹出框时触发过滤
      this.handleFilter()
    },
    
    handleCheckAllChange(field) {
      if (this.filterParams[field].length === this.filterOptions[field].length) {
        this.filterParams[field] = []
      } else {
        this.filterParams[field] = [...this.filterOptions[field]]
      }
      this.isIndeterminate[field] = false
      this.handleFilter()
    },
    
    handleCheckedChange(field) {
      const checkedCount = this.filterParams[field].length
      this.isIndeterminate[field] = checkedCount > 0 && checkedCount < this.filterOptions[field].length
      this.handleFilter()
    },
    
    handleFilterReset(field) {
      this.filterParams[field] = []
      this.isIndeterminate[field] = false
      this.handleFilter()
    },
    
    handleFilter() {
      // 构过滤参数
      const params = {}
      Object.entries(this.filterParams).forEach(([key, value]) => {
        if (Array.isArray(value) && value.length > 0) {
          params[key] = value
        } else if (typeof value === 'string' && value.trim()) {
          params[key] = value.trim()
        }
      })
      
      // 触发父组件的过滤事件
      this.$emit('filter-change', params)
    },
    
    toggleGameNameFilter() {
      this.showGameNameFilter = !this.showGameNameFilter
      if (this.showGameNameFilter) {
        this.$nextTick(() => {
          this.$refs.gameNameInput.focus()
        })
      }
    },
    
    hideGameNameFilter() {
      this.showGameNameFilter = false
    },
    
    getRankRateValue(value) {
      if (value === undefined || value === null) return 1
      // 确保返回的是数值类型
      const numValue = Number(value)
      // 如果转换失败，返回默认值 1
      return isNaN(numValue) ? 1 : numValue
    },
    
    getScoreRateValue(value) {
      if (value === undefined || value === null) return 0
      return Number(value)
    },
    handleShowReplay(row) {
      this.selectedGame = row
      this.replayViewVisible = true
    },
    handleReplayRefresh() {
      this.$emit('refresh')
    },
    
    // 处理题目操作完成事件
    async handleQuestionOperationComplete(data) {
      try {
        console.log('题目操作完成，开始刷新数据:', data)
        
        // 1. 清除缓存
        this.questionsCache.delete(data.gameCode)
        
        // 2. 如果当前行是展开状态，重新加载题目数据
        const row = this.gameList.find(item => item.game_code === data.gameCode)
        if (row && this.isExpanded(row)) {
          await this.loadQuestions(data.gameCode)
          
          // 3. 找到对应的题目列表组件并刷新
          const questionLists = this.$refs.questionList
          if (questionLists) {
            const lists = Array.isArray(questionLists) ? questionLists : [questionLists]
            for (const list of lists) {
              if (list && list.gameCode === data.gameCode) {
                await list.refresh()
              }
            }
          }
          
          // 4. 刷新表格布局
          this.$refs.table.doLayout()
        }
        
        // 5. 通知父组件刷新所有数据
        this.$emit('question-operation-complete', data)
      } catch (error) {
        console.error('处理题目操作完成事件失败:', error)
        this.$message.error('刷新数据失败')
      }
    },
    async handleEdit(row) {
      this.$emit('edit', row)
    },
    // 获取得分率的样式类
    getScoreRateClass(value, row) {
      // 检查是否有题目
      if (!row.question_num || row.question_num === 0) {
        return 'text-no-data';
      }
      if (value === undefined || value === null) return '';
      const score = Number(value);
      if (score === 100) return 'text-excellent';
      if (score >= 75) return 'text-good';
      if (score >= 50) return 'text-average';
      if (score >= 25) return 'text-pass';
      return 'text-fail';
    },
    // 获取排位的样式类
    getRankRateClass(value, row) {
      // 检查是否有参与数据
      if (!row.parameter_number || row.parameter_number === 0 || !row.rank || row.rank === 0) {
        return 'text-no-data';
      }
      
      if (value === undefined || value === null) return '';
      
      const rank = parseFloat(value);
      if (isNaN(rank)) return '';
      
      if (rank < 1) return 'text-excellent';
      if (rank <= 10) return 'text-good';
      if (rank <= 30) return 'text-average';
      if (rank <= 65) return 'text-pass';
      return 'text-fail';
    },
    // 获取得分率显示文本
    getScoreRateText(row) {
      if (!row.question_num || row.question_num === 0) {
        return '未添加';
      }
      return row.score_rate === undefined || row.score_rate === null ? 'Null' : 
        this.formatRankRate(row.score_rate);
    },
    // 获取排位显示文本
    getRankRateText(row) {
      // 检查是否有参与数据
      if (!row.parameter_number || row.parameter_number === 0) {
        return '无数据';
      }
      
      // 如果有人数但没有排名，也显示无数据
      if (!row.rank || row.rank === 0) {
        return '无数据';
      }
      
      // 处理正常情况
      if (row.rank_rate === undefined || row.rank_rate === null) {
        return 'Null';
      }

      // 计算实际比例
      const actualRate = (row.rank / row.parameter_number) * 100;
      console.log('actualRate:', actualRate, 'rank:', row.rank, 'total:', row.parameter_number);
      
      // 如果实际排名比例小于0.01%
      if (actualRate < 0.01) {
        return 'Top 0.01%';
      }
      
      return this.formatRankRate(row.rank_rate);
    },
    // 获取实际排名比例（用于父组件排序）
    getActualRankRate(row) {
      if (!row.parameter_number || row.parameter_number === 0 || !row.rank || row.rank === 0) {
        return Number.MAX_VALUE; // 无数据的排在最后
      }
      return (row.rank / row.parameter_number) * 100;
    },
    // 获取得分率标签类型
    getScoreRateTagType(value, row) {
      if (!row.question_num || row.question_num === 0) return 'info';
      if (value === undefined || value === null) return 'info';
      return 'plain';  // 使用朴素标签，样式由style控制
    },
    
    // 获取得分率标签样式
    getScoreRateStyle(value, row) {
      if (!row.question_num || row.question_num === 0) return {};
      if (value === undefined || value === null) return {};
      
      const rate = Number(value);
      if (isNaN(rate)) return {};
      
      if (rate === 100) return {
        backgroundColor: '#f0f9eb',
        borderColor: '#b3e19d',
        color: '#55a532'  // 更深的绿色
      };
      if (rate >= 75) return {
        backgroundColor: '#ecf5ff',
        borderColor: '#a3d0fd',
        color: '#2b85e4'  // 更深的蓝色
      };
      if (rate >= 50 && rate < 75) return {
        backgroundColor: '#fafdf5',
        borderColor: '#e7db5f',
        color: '#b58e05'  // 更深的黄色
      };
      if (rate >= 25 && rate < 50) return {
        backgroundColor: '#fef5eb',
        borderColor: '#ffd591',
        color: '#d48806'  // 更深的橙色
      };
      return {
        backgroundColor: '#fef0f0',
        borderColor: '#fbc4c4',
        color: '#cf1322'  // 更深的红色
      };
    },
    
    // 获取排位标签类型
    getRankRateTagType(value, row) {
      if (!row.parameter_number || row.parameter_number === 0 || !row.rank || row.rank === 0) return 'info';
      if (value === undefined || value === null) return 'info';
      return 'plain';  // 使用朴素标签，样式由style控制
    },
    
    // 获取排位标签样式
    getRankRateStyle(value, row) {
      if (!row.parameter_number || row.parameter_number === 0 || !row.rank || row.rank === 0) return {};
      if (value === undefined || value === null) return {};
      
      const rank = parseFloat(value);
      if (isNaN(rank)) return {};
      
      if (rank < 1) return {
        backgroundColor: '#f0f9eb',
        borderColor: '#b3e19d',
        color: '#55a532'  // 更深的绿色
      };
      if (rank > 1 && rank <= 10) return {
        backgroundColor: '#ecf5ff',
        borderColor: '#a3d0fd',
        color: '#2b85e4'  // 更深的蓝色
      };
      if (rank > 10 && rank <= 30) return {
        backgroundColor: '#fafdf5',
        borderColor: '#e7db5f',
        color: '#b58e05'  // 更深的黄色
      };
      if (rank > 30 && rank <= 65) return {
        backgroundColor: '#fef5eb',
        borderColor: '#ffd591',
        color: '#d48806'  // 更深的橙色
      };
      return {
        backgroundColor: '#fef0f0',
        borderColor: '#fbc4c4',
        color: '#cf1322'  // 更深的红色
      };
    },
    // 获取难度颜色
    getDifficultyColorByPercentage(percentage) {
      if (percentage === null || percentage === undefined) return '#909399'; // 默认灰色
      if (percentage <= 14.29) return '#F56C6C'; // 红题
      if (percentage <= 28.58) return '#E6A23C'; // 橙题
      if (percentage <= 42.87) return '#F7BA2A'; // 黄题
      if (percentage <= 57.15) return '#67C23A'; // 绿题
      if (percentage <= 71.44) return '#409EFF'; // 蓝题
      if (percentage <= 85.72) return '#8E44AD'; // 紫题
      return '#2C3E50'; // 黑题
    },
    // 格式化难度百分比
    formatDifficultyPercentage(percentage) {
      if (percentage === null || percentage === undefined) return '未定义';
      return `${percentage.toFixed(2)}%`;
    },
    // 获取分数颜色
    getScoreColor(score, questionNum) {
      if (!questionNum || questionNum === 0) return '#606266'; // 默认颜色，如果没有题目数
      
      const fullScore = questionNum * 100;
      const percentage = (score / fullScore) * 100;
      
      if (percentage >= 100) return '#237804'; // 绿色
      if (percentage >= 75) return '#52C41A'; // 黄绿色
      if (percentage >= 50) return '#F7BA2A'; // 黄色
      if (percentage >= 25) return '#E6A23C'; // 橙色
      return '#F56C6C'; // 红色
    }
  }
}
</script>

<style lang="scss" scoped>
.game-list {
  height: 100%;
  display: flex;
  flex-direction: column;
  
  .el-table {
    flex: 1;
    overflow: hidden;
    
    ::v-deep {
      .el-table__cell {
        border-right: none !important;
      }
      
      .el-table__row {
        border-bottom: 1px solid #EBEEF5;
      }
      
      .el-table__inner-wrapper {
        height: 100%;
      }
      
      .el-scrollbar__wrap {
        margin-right: 0 !important;
      }
      
      .el-table__body-wrapper {
        .el-scrollbar__wrap {
          margin-bottom: 0 !important;
        }
      }
    }
  }
  
  .pagination-container {
    height: 42px;
    padding: 1px 20px;
    background: #fff;
    border-top: 1px solid #e6e6e6;
    position: sticky;
    bottom: 0;
    left: 0;
    right: 0;
    z-index: 2;
    display: flex;
    justify-content: space-between;
    align-items: center;

    .pagination-left {
      display: flex;
      align-items: center;

      .el-button {
        margin-right: 16px;
        padding: 7px 15px;
        font-size: 13px;
        
        &.el-button--primary {
          background-color: #7B9DF3;
          border-color: #7B9DF3;
        }
      }
    }

    .el-pagination {
      margin-left: auto;
    }
  }
}

// 自定义全局覆盖样式
::v-deep {
  body .el-popover.column-selector-popover {
    padding-bottom: 12px !important;
    max-height: none !important;
    overflow: visible !important;
    
    .popover-content-wrapper {
      max-height: 60vh !important;
      overflow-y: auto !important;
      scrollbar-width: thin;
      scrollbar-color: #c1c1c1 #f1f1f1;
    }
    
    .el-scrollbar__wrap {
      overflow-y: auto !important;
      max-height: 60vh !important;
    }
  }
}

.game-name-cell {
  display: flex;
  align-items: center;
  gap: 4px;

  i {
    font-size: 16px;
    margin-right: 4px;
    flex-shrink: 0;
    
    // 链接图标（可触发）
    &.el-icon-position {
      color: #409EFF;
      cursor: pointer;
      
      &:hover {
        opacity: 0.8;
      }
    }
    
    // 复盘图标（不可触发）
    &.el-icon-video-camera {
      color: #606266;
      cursor: default;
      
      // 已完成状态
      &.completed {
        color: #409EFF;
        cursor: pointer;
        
        &:hover {
          opacity: 0.8;
        }
      }
    }
  }

  .game-name-text {
    flex: 1;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    
    .game-name-link {
      color: #409EFF;
      text-decoration: none;
      
      &:hover {
        text-decoration: underline;
      }
    }
    
    span {
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
  }
}

.operation-buttons {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 8px;
  
  .operation-button {
    width: 24px;
    height: 24px;
    padding: 0;
    border-radius: 4px;
    margin: 0;
    border: none;
    transition: all 0.2s ease;
    
    i {
      font-size: 14px;
      color: white;
    }
    
    // 展开按钮 - 蓝色
    &.expand-button {
      background-color: #409EFF;
      
      &:hover {
        background-color: #337ecc;
        transform: translateY(-1px);
        box-shadow: 0 2px 4px rgba(0,0,0,0.1);
      }
      
      &:active {
        transform: translateY(0);
        background-color: #2b6cb0;
      }
    }
    
    // 编辑按钮 - 深蓝色
    &.edit-button {
      background-color: #0056b3;
      
      &:hover {
        background-color: #004494;
        transform: translateY(-1px);
        box-shadow: 0 2px 4px rgba(0,0,0,0.1);
      }
      
      &:active {
        transform: translateY(0);
        background-color: #003d82;
      }
    }
    
    // 录题按钮 - 青色
    &.add-button {
      background-color: #17a2b8;
      
      &:hover {
        background-color: #138496;
        transform: translateY(-1px);
        box-shadow: 0 2px 4px rgba(0,0,0,0.1);
      }
      
      &:active {
        transform: translateY(0);
        background-color: #0f6674;
      }
    }
    
    // 复盘按钮 - 紫色
    &.replay-button {
      background-color: #6f42c1;
      
      &:hover {
        background-color: #5f3a9f;
        transform: translateY(-1px);
        box-shadow: 0 2px 4px rgba(0,0,0,0.1);
      }
      
      &:active {
        transform: translateY(0);
        background-color: #4e2a84;
      }
    }
    
    // 禁用状态按钮
    &.disabled-button {
      background-color: #F2F3F5;
      cursor: not-allowed;
      opacity: 0.6;
      
      i {
        color: #C0C4CC;
      }
      
      &:hover {
        background-color: #F2F3F5;
        transform: none;
        box-shadow: none;
      }
    }
    
    &:disabled {
      cursor: not-allowed;
      background-color: #F2F3F5;
      opacity: 0.6;
      
      i {
        color: #C0C4CC;
      }
      
      &:hover {
        transform: none;
        box-shadow: none;
      }
    }
  }
}

.column-settings {
  .column-form {
    .column-group {
      display: grid;
      grid-template-columns: repeat(3, 1fr);
      gap: 16px;
      
      .el-form-item {
        margin-bottom: 16px;
      }
    }
  }
}

.text-excellent { color: #67C23A; font-weight: 600; }
.text-good { color: #409EFF; font-weight: 500; }
.text-average { color: #E6A23C; font-weight: 500; }
.text-pass { color: #909399; }
.text-fail { color: #F56C6C; font-weight: 500; }
.text-no-data { color: #909399; font-style: italic; }

::v-deep .el-table {
  // 表格边框颜色
  border: 1px solid #DCDFE6;
  
  // 单元格边框颜色
  td, th {
    border-color: #DCDFE6;
  }
  
  // 表头样式
  th {
    background-color: #f2f3f5 !important;
    color: #606266;
    font-weight: 600;
    font-size: 14px;
    height: 48px;
    padding: 0;
    border-bottom: 1px solid #DCDFE6;
  }
  
  // 表格内容样式
  td {
    padding: 8px 0;
    font-size: 13px;
    color: #606266;
  }
  
  // 斑马纹样式
  .el-table__row--striped td {
    background-color: #fafafa;
  }
  
  // 鼠标悬停样式
  .el-table__row:hover td {
    background-color: #f0f7ff !important;
  }
  
  // 选中的行样式
  .el-table__row.current-row td {
    background-color: #ecf5ff !important;
  }
  
  // 固定列样式
  .el-table__fixed {
    height: 100% !important;
    bottom: 0;
    overflow: hidden;
    position: absolute;
    z-index: 3;
    background: #fff;
  }
  
  .el-table__fixed-right {
    right: 0;
    top: 0;
    height: 100% !important;
  }
  
  // 取消垂直网格线
  .el-table__cell {
    border-right: none !important;
  }
  
  // 操作列背景
  .operation-column .cell {
    background-color: rgba(248, 249, 250, 0.6) !important;
    padding: 8px 4px !important;
  }
}

.rank-rate-cell {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  
  .el-icon-medal-1 {
    color: #006400;
    font-size: 16px;
    transform: scale(1.2);
  }
}

.rank-rate-excellent {
  color: #006400;
  font-weight: 600;
}

.status-tag {
  width: 80px !important;
  height: 28px !important;
  line-height: 26px !important;
  padding: 0 8px !important;
  border-width: 1px !important;
  font-size: 13px !important;
  font-weight: 500 !important;
  border-radius: 4px !important;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1) !important;
  
  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.15) !important;
    transition: all 0.3s ease;
  }
}

.auto-width-tag {
  width: auto !important;
  min-width: 50px !important;
  padding: 0 8px !important;
  white-space: nowrap !important;
  display: inline-flex !important;
  justify-content: center !important;
  align-items: center !important;
  height: 28px !important;
  line-height: 26px !important;
  font-size: 13px !important;
  border-radius: 4px !important;
  box-sizing: border-box !important;
  transition: all 0.3s !important;
  
  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }
}

::v-deep {
  .score-rate-column,
  .rank-rate-column,
  .fix-status-column,
  .replay-status-column,
  .difficulty-column {
    .cell {
      padding: 4px !important;
      display: flex !important;
      justify-content: center !important;
      align-items: center !important;
      height: 100% !important;
      
      .el-tag {
        margin: 0 !important;
        flex: 0 0 auto !important;
      }
    }
  }
  
  .el-table__body td {
    padding: 8px 0 !important;
  }
  
  .el-table__cell {
    .cell {
      overflow: visible !important;
    }
  }
}

::v-deep .column-selector-popover {
  max-height: 60vh !important;
  overflow: visible !important;
  
  .el-popover__content {
    max-height: 60vh;
    overflow: visible;
  }
  
  .el-form {
    margin: 0;
    padding: 0;
  }
  
  .popover-content-wrapper {
    max-height: 60vh !important;
    overflow-y: auto !important;
    
    &::-webkit-scrollbar {
      width: 6px;
    }
    
    &::-webkit-scrollbar-track {
      background: #f1f1f1;
      border-radius: 3px;
    }
    
    &::-webkit-scrollbar-thumb {
      background: #c1c1c1;
      border-radius: 3px;
    }
    
    &::-webkit-scrollbar-thumb:hover {
      background: #a8a8a8;
    }
  }
  
  .column-options {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 8px;
    padding: 10px;
    
    @media screen and (max-width: 768px) {
      grid-template-columns: 1fr;
    }
    
    .el-form-item {
      margin-bottom: 0;
      
      .el-form-item__label {
        line-height: 32px;
        padding: 0;
        font-size: 13px;
      }
      
      .el-form-item__content {
        line-height: 32px;
      }
    }
  }
  
  .el-popover__title {
    font-size: 16px;
    font-weight: 500;
    margin-bottom: 12px;
  }
}

::v-deep .difficulty-tooltip {
  max-width: 350px !important;
  min-width: 320px !important;
  padding: 0 !important;
  border-radius: 6px !important;
}

::v-deep .difficulty-tooltip-content {
  padding: 15px;
}

::v-deep .difficulty-description {
  margin-bottom: 12px;
}

::v-deep .difficulty-description p {
  margin: 0 0 6px 0;
  line-height: 1.5;
  font-size: 13px;
  text-align: left;
}

::v-deep .difficulty-levels {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

::v-deep .difficulty-level {
  display: flex;
  align-items: center;
  text-align: left;
  font-size: 13px;
}

::v-deep .level-color {
  display: inline-block;
  width: 16px;
  height: 16px;
  border-radius: 3px;
  margin-right: 8px;
}

::v-deep .level-range {
  width: 130px;
  display: inline-block;
  font-weight: bold;
}

::v-deep .level-name {
  flex: 1;
}
</style>